import { Injectable } from '@angular/core';
import { HttpContextToken, HttpEvent, HttpEventType, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { AuthService } from './auth.service';
import { finalize } from 'rxjs/operators';
import { SpinnerService } from '../_shared/components/spinner/spinner.service';
import { AlertController } from '@ionic/angular';
import { SharedService } from '../_shared/services/shared.service';
import { TranslateService } from '@ngx-translate/core';

export const SHOW_SPINNER_HTTP_TOKEN = new HttpContextToken<boolean>(() => true);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private spinnerCount = 0;

  constructor(private authService: AuthService, private sharedService: SharedService, private spinner: SpinnerService, private translate: TranslateService, private alertController: AlertController) {
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const requestWithSpinner = request.context.get(SHOW_SPINNER_HTTP_TOKEN);

    if (requestWithSpinner) {
      if (this.spinnerCount === 0) {
        this.spinner.show();
      }

      this.spinnerCount++;
    }

    // add auth header with jwt if user is logged in and request is to the api url
    const currentUser = this.authService.currentUserValue;

    if (currentUser != null && currentUser.accessToken != null) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${currentUser.accessToken}`
        }
      });
    }

    return next.handle(request).pipe(tap((event) => {
        if (event.type == HttpEventType.Response) {
          if (event.url != null && event.url.includes('/api/')) {
            this.checkNewVersion(event.headers);
          }
        }
      }), finalize(() => {
      if (requestWithSpinner) {
        this.spinnerCount--;

        if (this.spinnerCount === 0) {
          this.spinner.hide();
        }
      }
    }));
  }

  private checkNewVersion(headers: HttpHeaders): void {
    const appId = headers.get('App-ID');

    if (appId != null) {
      if (!this.sharedService.isVersionModalOpen && !this.sharedService.updateVersionLater && this.sharedService.appVersion != null && this.sharedService.appVersion !== appId) {
        this.sharedService.isVersionModalOpen = true;

        this.presentNewVersionAlert();
      }

      this.sharedService.appVersion = appId;
    }
  }

  async presentNewVersionAlert() {
    const alert = await this.alertController.create({
      header: this.translate.instant('COMMON.NEW_VERSION'),
      message: this.translate.instant('COMMON.NEW_VERSION_INFO'),
      backdropDismiss: false,
      buttons: [
        {
          text: this.translate.instant('COMMON.LATER'),
          role: 'later'
        },
        {
          text: this.translate.instant('COMMON.PAGE_REFRESH'),
          role: 'refresh'
        },
      ],
    });

    await alert.present();

    const { role } = await alert.onDidDismiss();

    if (role === 'later') {
      this.sharedService.updateVersionLater = true;
    } else if (role === 'refresh') {
      window.location.reload();
    }
  }
}
